import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { IOSSwitch, OutlinedButton } from '../../../../component/buttonComponent/ButtonComponent';
import { EVENT_COLOR } from '../../../../component/mapTripReport/mapColors';
import DateTimePicker from '../../../../component/dateRangePicker/DateTimePicker';
import { compactDuration } from '../odAnalyticsUtils';
import OdSelect from './OdSelect';
import {
    COMMIT_MS,
    MOTION_STATES,
    PLAY_RATE,
    PLAY_RATES,
    TAPE_EVENTS,
    buildRibbon,
    buildSpeedPoints,
    clampWindow,
    formatTapeClock,
    formatTapeInput,
    normaliseSamples,
    parseTapeInput,
    positionAt,
    snapTimeIntoWindow,
    speedCeiling,
    xPct,
} from '../odTapeUtils';

/**
 * The trip tape: one trip's telemetry as a scrubbable timeline.
 *
 * Rendered as plain DOM — percentage-positioned spans for the state ribbon, one SVG polyline for the
 * speed lane, and a playhead moved by `transform`. No canvas (jsdom has no 2D context, so nothing
 * would be testable) and no chart library (a playhead as an ApexCharts annotation means clearing
 * and re-adding an annotation every frame, which is worse than the canvas it would replace).
 *
 * **Two clocks, deliberately decoupled.** `scrubRef` holds the exact playhead time and is the only
 * authority. Every mover goes through `commit`, which moves the playhead imperatively — a CSS
 * transform and an uncontrolled input's value, neither of which React owns — and only then, at most
 * every COMMIT_MS, re-renders the readout and notifies the map. At 30x playback that is ~12 React
 * commits a second instead of 60, and the visible playhead is still smooth because it never waited
 * for React in the first place.
 *
 * The pointer surface is a transparent `<input type="range">` covering the whole tape, exactly as
 * the prototype does it. One target, no hit-testing maths, and arrow keys / Home / End / touch all
 * work for free.
 */
const TripTape = (props) => {
    const {
        trip,
        trail,
        loading,
        error,
        notFound,
        tooLarge,
        events,
        episodes,
        timezone,
        follow,
        onToggleFollow,
        onScrub,
        onUserSeek,
    } = props;

    const [windowRange, setWindowRange] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [cursorMs, setCursorMs] = useState(null);
    const [jumpValue, setJumpValue] = useState('');
    const [jumpError, setJumpError] = useState('');
    const [zoomFrom, setZoomFrom] = useState('');
    const [zoomTo, setZoomTo] = useState('');
    const [zoomError, setZoomError] = useState('');
    // Mirrors rateRef so the Horizon select has a value to show; rateRef stays the loop's source.
    const [rate, setRate] = useState(PLAY_RATE);

    const scrubRef = useRef(0);
    const playheadRef = useRef(null);
    const rangeRef = useRef(null);
    const rafRef = useRef(null);
    const lastCommit = useRef(0);
    // A ref, not state: the rate is read once per frame inside the loop and shown nowhere, so
    // storing it in React would re-render the whole tape to change a number nothing displays.
    // Changing the select mid-playback therefore takes effect on the very next frame.
    const rateRef = useRef(PLAY_RATE);

    const rows = useMemo(() => normaliseSamples(trail?.samples), [trail]);
    const tripId = trip?.startTraceId;

    const tripFromMs = rows.length > 0 ? rows[0].ms : 0;
    const tripToMs = rows.length > 0 ? rows[rows.length - 1].ms : 0;
    const fromMs = windowRange ? windowRange.fromMs : tripFromMs;
    const toMs = windowRange ? windowRange.toMs : tripToMs;

    /**
     * The controls below pick a clock, not a date: one trip is the whole context, so asking for
     * the day again is a question already answered. The bounds are the trip's, not the zoomed
     * window's — zooming is how you choose a stretch, and it cannot be the thing that limits
     * which stretch you may choose.
     */
    const intoTrip = (next) => snapTimeIntoWindow(
        next, { fromMs: tripFromMs, toMs: tripToMs, timezone },
    );

    const ceiling = useMemo(() => speedCeiling(rows), [rows]);
    const ribbon = useMemo(() => buildRibbon(rows), [rows]);
    const speedPoints = useMemo(
        () => buildSpeedPoints(rows, fromMs, toMs, ceiling),
        [rows, fromMs, toMs, ceiling],
    );

    const tripEvents = useMemo(
        () => (events || []).filter((row) => row.tripId === tripId && row.startTime),
        [events, tripId],
    );
    const tripEpisodes = useMemo(
        () => (episodes || []).filter((row) => row.tripId === tripId && row.startTime),
        [episodes, tripId],
    );

    /** Single writer for the playhead. `force` bypasses the throttle for the final frame. */
    const commit = useCallback((ms, { force = false } = {}) => {
        const clamped = Math.min(Math.max(ms, fromMs), toMs);
        scrubRef.current = clamped;

        const pct = xPct(clamped, fromMs, toMs);
        // Compositor-only: no layout, no paint, no React.
        if (playheadRef.current) playheadRef.current.style.transform = `translateX(${pct}%)`;
        // Uncontrolled on purpose — a controlled value would fight this write.
        if (rangeRef.current) rangeRef.current.value = String(Math.round(pct * 10));

        // Date.now, not performance.now: a throttle only needs wall-clock milliseconds, and this is
        // the clock the whole codebase (and jest's fake timers) already agree on. A clock step would
        // at worst cost one extra commit.
        const now = Date.now();
        if (!force && now - lastCommit.current < COMMIT_MS) return;
        lastCommit.current = now;
        setCursorMs(clamped);
        if (typeof onScrub === 'function') onScrub(positionAt(rows, clamped));
    }, [fromMs, toMs, rows, onScrub]);

    const stopPlay = useCallback(() => {
        setPlaying(false);
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    // A new trip always opens un-zoomed, at its start.
    useEffect(() => {
        stopPlay();
        setWindowRange(null);
        setJumpError('');
        setZoomError('');
        setJumpValue('');
    }, [tripId, stopPlay]);

    /**
     * Park the cursor at the window start whenever the window itself changes.
     *
     * This commit is initialisation, not the vehicle moving — the map has to tell the two apart or
     * it will chase the playhead to the trip's start the moment a trail lands. See the follow
     * effect in mapTripReport.
     */
    useEffect(() => {
        if (rows.length === 0) return;
        lastCommit.current = 0;
        commit(fromMs, { force: true });
        setZoomFrom(formatTapeInput(fromMs, timezone));
        setZoomTo(formatTapeInput(toMs, timezone));
        // NOTE: `commit` is intentionally not a dependency — it changes identity with every window
        // change, and re-running this effect on that would fight the playback loop.
    }, [tripId, fromMs, toMs, rows.length, timezone]);

    useEffect(() => () => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    }, []);

    const togglePlay = useCallback(() => {
        // Driving the tape releases any pinned point, so the map follows the vehicle again — see
        // the follow effect in mapTripReport, where a pin outranks the cursor.
        if (typeof onUserSeek === 'function') onUserSeek();
        if (playing) {
            stopPlay();
            return;
        }
        if (rows.length === 0) return;

        // Parked at the end, Play means replay. The prototype's loop satisfied its stop condition
        // on the first frame here and did nothing at all.
        if (toMs - scrubRef.current < 100) commit(fromMs, { force: true });

        setPlaying(true);
        let last = null;
        const step = (now) => {
            // First frame establishes the baseline; rAF hands us its own monotonic timestamp.
            if (last === null) last = now;
            const dt = (now - last) * rateRef.current;
            last = now;
            // Accumulate from the exact time, never from the throttled or rounded value — those
            // quantise, and the error compounds over a minute of playback.
            const next = scrubRef.current + dt;
            if (next >= toMs) {
                commit(toMs, { force: true });
                stopPlay();
                return;
            }
            commit(next);
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
    }, [playing, rows.length, fromMs, toMs, commit, stopPlay, onUserSeek]);

    const onScrubInput = (event) => {
        if (typeof onUserSeek === 'function') onUserSeek();
        stopPlay();
        commit(fromMs + ((toMs - fromMs) * Number(event.target.value)) / 1000, { force: true });
    };

    const onJump = () => {
        const ms = parseTapeInput(jumpValue, timezone);
        if (!Number.isFinite(ms)) {
            setJumpError(`Use YYYY-MM-DD HH:MM:SS in the report timezone (${timezone}).`);
            return;
        }
        if (ms < tripFromMs || ms > tripToMs) {
            setJumpError(`Outside this trip (${formatTapeClock(tripFromMs, timezone)} → ${formatTapeClock(tripToMs, timezone)}).`);
            return;
        }
        setJumpError('');
        if (typeof onUserSeek === 'function') onUserSeek();
        stopPlay();
        // Widen out of a zoom rather than snapping to its edge: the prototype accepted a valid
        // in-trip time, clamped it to the window, and then flew the map to the unclamped point, so
        // the marker and the playhead disagreed with no warning.
        if (windowRange && (ms < windowRange.fromMs || ms > windowRange.toMs)) setWindowRange(null);
        commit(ms, { force: true });
    };

    const onApplyZoom = () => {
        const { window: next, error: message } = clampWindow(
            parseTapeInput(zoomFrom, timezone),
            parseTapeInput(zoomTo, timezone),
            tripFromMs,
            tripToMs,
        );
        if (!next) {
            setZoomError(message);
            return;
        }
        setZoomError('');
        stopPlay();
        setWindowRange(next);
    };

    const resetZoom = () => {
        setZoomError('');
        stopPlay();
        setWindowRange(null);
    };

    // ---- states that render instead of a tape --------------------------------

    const shell = (title, body) => (
        <div className="od-card">
            <div className="od-card-header">
                <div className="od-card-header-text">
                    <div className="od-card-title">Trip tape</div>
                    <div className="od-card-subtitle">
                        Scrub, replay and read speed, state and fuel across the trip
                    </div>
                </div>
            </div>
            <div className="od-empty">
                {title ? <div className="od-empty-title">{title}</div> : null}
                <div className="od-empty-text">{body}</div>
            </div>
        </div>
    );

    if (!trip) return shell(null, 'Select a trip to scrub its telemetry.');
    if (loading) return shell(null, 'Loading this trip’s telemetry…');
    if (notFound) return shell('Trip not available', 'This trip is no longer visible to your agency.');
    if (tooLarge) return shell('Too much telemetry', error || 'This trip holds more samples than the trail cap.');
    if (error) return shell('Could not load the telemetry', error);
    if (rows.length === 0) {
        return shell(
            'No telemetry for this trip',
            'The trip exists but carried no telemetry samples, so there is nothing to scrub.',
        );
    }

    // ---- readout ------------------------------------------------------------

    const at = positionAt(rows, cursorMs ?? fromMs);
    const state = at && at.code !== null ? MOTION_STATES[at.code] : null;
    const episodeAtCursor = at
        ? tripEpisodes.find((row) => {
            const start = Date.parse(row.startTime);
            const end = Date.parse(row.endTime);
            return at.ms >= start && at.ms <= (Number.isFinite(end) ? end : start);
        })
        : null;
    const zoomed = Boolean(windowRange);

    return (
        <div className="od-card">
            <div className="od-card-header">
                <div className="od-card-header-text">
                    <div className="od-card-title">
                        Trip tape
                        {zoomed && (
                            <span className="od-badge od-badge-neutral">
                                {`Zoomed to ${compactDuration(Math.round((toMs - fromMs) / 1000))} of ${compactDuration(Math.round((tripToMs - tripFromMs) / 1000))}`}
                            </span>
                        )}
                    </div>
                    <div className="od-card-subtitle">
                        {`${trail?.sampleCount ?? rows.length} samples${trail?.cadenceSec ? ` at ~${trail.cadenceSec}s cadence` : ''}`}
                    </div>
                </div>
                <div className="od-card-actions">
                    <div className="od-map-control-row">
                        <span className="od-map-control-text">Follow vehicle</span>
                        <IOSSwitch checked={Boolean(follow)} onChange={onToggleFollow} />
                    </div>
                </div>
            </div>

            <div className="od-tape-readout">
                <span className="od-tape-readout-time">{formatTapeClock(at?.ms, timezone)}</span>
                <span className="od-tape-readout-item">
                    <b>{at?.kmh === null || at?.kmh === undefined ? '—' : Math.round(at.kmh)}</b>
                    {' km/h'}
                </span>
                {state && (
                    <span className="od-tape-readout-item">
                        <span className={`od-tape-chip od-tape-state-${state.cls}`} />
                        {state.label}
                    </span>
                )}
                {at?.litre !== null && at?.litre !== undefined && (
                    <span className="od-tape-readout-item">
                        <b>{at.litre}</b>
                        {' L fuel'}
                    </span>
                )}
                {/* Its own column, not a conversion of the litres — an ELD asset reports the
                    percentage and nothing else, so this is the only fuel signal it has. */}
                {at?.pct !== null && at?.pct !== undefined && (
                    <span className="od-tape-readout-item">
                        <b>{Math.round(at.pct)}</b>
                        % fuel
                    </span>
                )}
                {at?.soc !== null && at?.soc !== undefined && (
                    <span className="od-tape-readout-item">
                        <b>{at.soc}</b>
                        % SOC
                    </span>
                )}
                {episodeAtCursor && (
                    <span className="od-badge od-badge-warn">
                        {`${episodeAtCursor.classCode} ${MOTION_STATES[episodeAtCursor.classCode]?.label || 'Idle'} · ${compactDuration(episodeAtCursor.durationSec)}`}
                    </span>
                )}
                {at?.lat !== null && at?.lat !== undefined && (
                    <span className="od-tape-readout-coords">
                        {`${at.lat.toFixed(6)}, ${at.lon.toFixed(6)}`}
                    </span>
                )}
                {(at?.reason === 'gap' || at?.reason === 'no-fix') && (
                    <span
                      className="od-tape-readout-warn"
                      title="The samples either side of the cursor straddle a data gap, so the position shown is the last known fix."
                    >
                        ⚠ last known fix
                    </span>
                )}
            </div>

            <div className="od-tape-track">
                {/* Bands first, then the speed lane over them, then event ticks, then the
                    playhead — the playhead must never be occluded. */}
                {ribbon.map((band) => {
                    const left = xPct(band.fromMs, fromMs, toMs);
                    const width = xPct(band.toMs, fromMs, toMs) - left;
                    if (width <= 0) return null;
                    const cls = band.code !== null && MOTION_STATES[band.code]
                        ? `od-tape-state-${MOTION_STATES[band.code].cls}`
                        : 'od-tape-state-unknown';
                    return (
                        <span
                          key={band.fromMs}
                          className={`od-tape-band ${cls}${band.unobserved ? ' od-tape-band-unobserved' : ''}`}
                          style={{ left: `${left}%`, width: `${Math.max(width, 0.1)}%` }}
                          title={band.unobserved ? 'No telemetry across this stretch' : MOTION_STATES[band.code]?.label}
                        />
                    );
                })}

                <svg
                  className="od-tape-speed"
                  viewBox="0 0 1000 100"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                    {/* Without non-scaling-stroke the non-uniform scale smears 1px into a wedge. */}
                    <polyline points={speedPoints} vectorEffect="non-scaling-stroke" />
                </svg>

                {tripEvents.map((row) => {
                    const ms = Date.parse(row.startTime);
                    const endMs = Date.parse(row.endTime);
                    const left = xPct(ms, fromMs, toMs);
                    const meta = TAPE_EVENTS[row.type];
                    return (
                        <React.Fragment key={row.id}>
                            {row.type === 'OVERSPEED' && Number.isFinite(endMs) && endMs > ms && (
                                <span
                                  className="od-tape-overspeed"
                                  style={{
                                      left: `${left}%`,
                                      width: `${Math.max(xPct(endMs, fromMs, toMs) - left, 0.2)}%`,
                                  }}
                                />
                            )}
                            <span
                              className="od-tape-tick"
                              style={{ left: `${left}%`, borderBottomColor: EVENT_COLOR[row.type] }}
                              title={`${meta?.label || row.type} · ${formatTapeClock(ms, timezone)}`}
                            />
                        </React.Fragment>
                    );
                })}

                <span className="od-tape-playhead-track" ref={playheadRef}>
                    <span className="od-tape-playhead" />
                </span>

                <input
                  className="od-tape-scrubber"
                  ref={rangeRef}
                  type="range"
                  min="0"
                  max="1000"
                  step="1"
                  defaultValue={0}
                  onInput={onScrubInput}
                  onChange={onScrubInput}
                  aria-label="Trip timeline scrubber"
                />
            </div>

            <div className="od-tape-axis">
                <span>{formatTapeClock(fromMs, timezone)}</span>
                <span>{formatTapeClock(toMs, timezone)}</span>
            </div>

            <div className="od-tape-controls">
                <OutlinedButton
                  label={playing ? 'Pause' : 'Play'}
                  width="5.5rem"
                  onClickFunction={togglePlay}
                />
                {/* The loop still reads rateRef every frame — the state only exists because the
                    Horizon select is controlled, and it changes on a click, not on a tick. */}
                <OdSelect
                  label="Speed"
                  value={String(rate)}
                  ariaLabel="Playback speed"
                  onChange={(value) => {
                      setRate(Number(value));
                      rateRef.current = Number(value);
                  }}
                  options={PLAY_RATES.map((value) => ({ value: String(value), label: `×${value}` }))}
                />
                <span className="od-tape-field">
                    <span>Jump to</span>
                    <DateTimePicker
                      compact
                      timeOnly
                      ariaLabel="Jump to"
                      value={jumpValue}
                      onChange={(next) => setJumpValue(intoTrip(next))}
                      min={formatTapeInput(tripFromMs, timezone)}
                      max={formatTapeInput(tripToMs, timezone)}
                      invalid={Boolean(jumpError)}
                    />
                </span>
                <OutlinedButton label="Go" width="4rem" onClickFunction={onJump} />
                {jumpError ? <span className="od-tape-error">{jumpError}</span> : null}

                <span className="od-tape-controls-spacer" />

                <span className="od-tape-field">
                    <span>Zoom</span>
                    <DateTimePicker
                      compact
                      timeOnly
                      ariaLabel="Zoom from"
                      value={zoomFrom}
                      onChange={(next) => setZoomFrom(intoTrip(next))}
                      min={formatTapeInput(tripFromMs, timezone)}
                      max={formatTapeInput(tripToMs, timezone)}
                      invalid={Boolean(zoomError)}
                    />
                    <span aria-hidden="true">→</span>
                    <DateTimePicker
                      compact
                      timeOnly
                      ariaLabel="Zoom to"
                      value={zoomTo}
                      onChange={(next) => setZoomTo(intoTrip(next))}
                      min={formatTapeInput(tripFromMs, timezone)}
                      max={formatTapeInput(tripToMs, timezone)}
                      invalid={Boolean(zoomError)}
                    />
                </span>
                <OutlinedButton label="Zoom" width="5rem" onClickFunction={onApplyZoom} />
                {zoomed && <OutlinedButton label="Full trip" width="6rem" onClickFunction={resetZoom} />}
                {zoomError ? <span className="od-tape-error">{zoomError}</span> : null}
            </div>

            {trail?.availability?.kmh === 'unpopulated' && (
                <div className="od-card-note">
                    This asset reported no speed on this trip, so the speed trace is flat — that is a
                    missing signal, not a stationary vehicle.
                </div>
            )}
        </div>
    );
};

export default TripTape;
